import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Package, ShoppingBag, Users, Truck, FileText, Inbox, Clock, CheckCircle2, TrendingUp, ArrowRight, Store, UserCircle } from "lucide-react";

function StatCard({ icon: Icon, label, value, hint, testid, to }) {
  const body = (
    <div className="bg-white border border-border rounded-lg p-6 card-lift h-full" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-md bg-primary/10 text-primary grid place-items-center"><Icon className="w-5 h-5" /></div>
        {to && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="mt-6 overline text-muted-foreground">{label}</div>
      <div className="mt-1 font-heading text-4xl font-black text-secondary tracking-tight">{value}</div>
      {hint && <div className="mt-2 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function PendingBanner() {
  return (
    <div className="mb-6 p-5 rounded-lg border border-primary/30 bg-primary/5" data-testid="pending-banner">
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 text-primary mt-0.5" />
        <div>
          <div className="font-semibold text-secondary">Account under review</div>
          <div className="text-sm text-muted-foreground mt-1">Our team is reviewing your application. You'll receive an email once approved. In the meantime you can still update your profile.</div>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(()=>{}); }, []);
  const s = stats || {};
  return (
    <DashboardLayout title="Overview">
      <div className="mb-8">
        <div className="overline text-primary">CONTROL ROOM</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-secondary mt-2">The whole operation at a glance.</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-pending-users" icon={Users} label="Pending approvals" value={s.pending_users ?? "—"} hint="Users awaiting review" to="/admin/users?status=pending" />
        <StatCard testid="stat-submitted-products" icon={Package} label="Products to review" value={s.submitted_products ?? "—"} hint="Submitted by suppliers" to="/admin/products?status=submitted" />
        <StatCard testid="stat-open-quotes" icon={FileText} label="Open quote requests" value={s.open_quotes ?? "—"} to="/admin/quotes" />
        <StatCard testid="stat-active-shipments" icon={Truck} label="Active shipments" value={s.active_shipments ?? "—"} to="/admin/shipments" />
        <StatCard testid="stat-new-leads" icon={Inbox} label="New leads" value={s.new_leads ?? "—"} to="/admin/leads" />
        <StatCard testid="stat-approved-products" icon={CheckCircle2} label="Live products" value={s.approved_products ?? "—"} />
        <StatCard testid="stat-suppliers" icon={TrendingUp} label="Active suppliers" value={s.total_suppliers ?? "—"} />
        <StatCard testid="stat-buyers" icon={ShoppingBag} label="Active buyers" value={s.total_buyers ?? "—"} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 card-lift">
          <CardHeader><CardTitle className="font-heading tracking-tight">Quick actions</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Link to="/admin/users"><Button variant="outline" className="w-full justify-start" data-testid="qa-users"><Users className="w-4 h-4 mr-2"/>Review users</Button></Link>
            <Link to="/admin/products"><Button variant="outline" className="w-full justify-start" data-testid="qa-products"><Package className="w-4 h-4 mr-2"/>Review products</Button></Link>
            <Link to="/admin/quotes"><Button variant="outline" className="w-full justify-start" data-testid="qa-quotes"><FileText className="w-4 h-4 mr-2"/>Process quotes</Button></Link>
            <Link to="/admin/shipments"><Button variant="outline" className="w-full justify-start" data-testid="qa-shipments"><Truck className="w-4 h-4 mr-2"/>Update shipments</Button></Link>
            <Link to="/admin/leads"><Button variant="outline" className="w-full justify-start" data-testid="qa-leads"><Inbox className="w-4 h-4 mr-2"/>Track leads</Button></Link>
            <Link to="/messages"><Button variant="outline" className="w-full justify-start" data-testid="qa-messages"><ArrowRight className="w-4 h-4 mr-2"/>Open inbox</Button></Link>
          </CardContent>
        </Card>
        <Card className="card-lift">
          <CardHeader><CardTitle className="font-heading tracking-tight">Compliance checklist</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <div>Standard fields reviewed on every product:</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>HS code + origin</li>
              <li>Certifications & test reports</li>
              <li>Product imagery</li>
              <li>Supplier due-diligence</li>
              <li>Labelling & language</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function SupplierDashboard({ user }) {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/supplier/products").then((r) => setProducts(r.data)).catch(()=>{}); }, []);
  const draft = products.filter((p) => p.status === "draft").length;
  const submitted = products.filter((p) => p.status === "submitted" || p.status === "under_review").length;
  const approved = products.filter((p) => p.status === "approved").length;
  const rejected = products.filter((p) => p.status === "rejected").length;
  return (
    <DashboardLayout title="Supplier overview" actions={
      <div className="flex gap-2 flex-wrap">
        <a href={`/suppliers/${user.id}`} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" data-testid="view-public-shop-btn"><Store className="w-4 h-4 mr-2" />View my public shop</Button>
        </a>
        <Link to="/supplier/profile"><Button variant="outline" data-testid="edit-profile-btn"><UserCircle className="w-4 h-4 mr-2" />Edit profile</Button></Link>
        <Link to="/supplier/products/new"><Button data-testid="new-product-btn">+ New product</Button></Link>
      </div>
    }>
      {user.status !== "approved" && <PendingBanner />}
      <div className="mb-8">
        <div className="overline text-primary">SUPPLIER PORTAL</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-secondary mt-2">Hello, {user.name}.</h2>
        <p className="text-muted-foreground mt-2">Submit new products, track compliance, and manage documents.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-drafts" icon={FileText} label="Drafts" value={draft} />
        <StatCard testid="stat-submitted" icon={Clock} label="Under review" value={submitted} />
        <StatCard testid="stat-approved" icon={CheckCircle2} label="Live" value={approved} to="/supplier/products" />
        <StatCard testid="stat-rejected" icon={Inbox} label="Rejected" value={rejected} />
      </div>
      <Card className="mt-10 card-lift">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading tracking-tight">Recent products</CardTitle>
          <Link to="/supplier/products" className="text-sm text-primary hover:underline" data-testid="see-all-products">See all</Link>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">No products yet. <Link to="/supplier/products/new" className="text-primary hover:underline">Create your first product</Link></div>
          ) : (
            <div className="divide-y divide-border">
              {products.slice(0, 6).map((p) => (
                <Link to={`/supplier/products/${p.id}`} key={p.id} className="py-3 flex items-center justify-between hover:bg-muted/30 px-2 rounded-md" data-testid={`recent-product-${p.id}`}>
                  <div><div className="font-medium text-secondary">{p.name}</div><div className="text-xs text-muted-foreground">{p.category} · {p.origin_country}</div></div>
                  <Badge variant="outline" className="capitalize">{p.status.replace("_", " ")}</Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}

function BuyerDashboard({ user }) {
  const [quotes, setQuotes] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [shipments, setShipments] = useState([]);
  useEffect(() => {
    api.get("/buyer/quotes").then((r) => setQuotes(r.data)).catch(()=>{});
    api.get("/buyer/inquiries").then((r) => setInquiries(r.data)).catch(()=>{});
    api.get("/shipments").then((r) => setShipments(r.data)).catch(()=>{});
  }, []);
  return (
    <DashboardLayout title="Buyer overview" actions={<Link to="/buyer/catalog"><Button data-testid="browse-catalog-btn">Browse catalog</Button></Link>}>
      {user.status !== "approved" && <PendingBanner />}
      <div className="mb-8">
        <div className="overline text-primary">BUYER PORTAL</div>
        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-secondary mt-2">Welcome, {user.name}.</h2>
        <p className="text-muted-foreground mt-2">Browse vetted Asian products, request quotes, and track shipments.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-my-quotes" icon={FileText} label="Quote requests" value={quotes.length} to="/buyer/quotes" />
        <StatCard testid="stat-my-inquiries" icon={Inbox} label="Bulk inquiries" value={inquiries.length} to="/buyer/inquiries" />
        <StatCard testid="stat-active-shipments" icon={Truck} label="Shipments" value={shipments.length} to="/buyer/shipments" />
        <StatCard testid="stat-open-quotes" icon={Clock} label="Awaiting response" value={quotes.filter(q=>["new","in_review"].includes(q.status)).length} />
      </div>
      <Card className="mt-10 card-lift">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading tracking-tight">Recent quote requests</CardTitle>
          <Link to="/buyer/quotes" className="text-sm text-primary hover:underline" data-testid="see-all-quotes">See all</Link>
        </CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">No quotes yet. <Link to="/buyer/catalog" className="text-primary hover:underline">Browse the catalog</Link></div>
          ) : (
            <div className="divide-y divide-border">
              {quotes.slice(0, 6).map((q) => (
                <div key={q.id} className="py-3 flex items-center justify-between px-2" data-testid={`recent-quote-${q.id}`}>
                  <div><div className="font-medium text-secondary">{q.product_name}</div><div className="text-xs text-muted-foreground">Qty {q.quantity} · {q.destination_country || "—"}</div></div>
                  <Badge variant="outline" className="capitalize">{q.status.replace("_", " ")}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "supplier") return <SupplierDashboard user={user} />;
  return <BuyerDashboard user={user} />;
}
